import React, { useState, useEffect, useCallback } from "react";
import { Backdrop, FormControlLabel, Radio, RadioGroup, Tab, Tabs } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Theme, createStyles, makeStyles, withStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../utils/api";
import {
	BBI_CORE_ENROLLMENT_SHORT_NAMES,
	BBI_VEB_ENROLLMENT_SHORT_NAMES
} from "../../../../../../constants/BBIEnrollmentPlansShortName";
import { zipToCoordinateConverter } from "../../../../../../utils/commonFunctions/zipToCoordinateConverter";
import { CustomMapView } from "../../../../../shared";
import ScrollTabPanel from "../../../../../shared/tabPanelComponent/ScrollTabPanel";
import a11yScrollableProps from "../../../../../../constants/tabPanelProps/ayScrollableProps";
import { MagnifyingGlass, ThreeCircles } from "react-loader-spinner";
import {
	CoreMarkers,
	EnrollmentMapSectionProps,
	VEBMapMarkers
} from "../../../../../../@types/components/enrollmentMapSection.types";

import "./enrollmentMapSection.css";

const GreenSwitch = withStyles({
	switchBase: {
		color: "white",
		"&$checked": {
			color: "white"
		},
		"&$checked + $track": {
			backgroundColor: "white"
		}
	},
	checked: {},
	track: {
		backgroundColor: "white"
	}
})(Switch);

const GreenRadio = withStyles({
	root: {
		color: "#FFBF00",
		"&$checked": {
			color: "#FFBF00"
		}
	},
	checked: {}
})((props: any) => <Radio color="default" {...props} />);

const tabStyles = makeStyles({
	customTab: {
		minWidth: "180px !important" // Adjust the width as needed
	}
});

const backdropStyles = makeStyles((theme: Theme) =>
	createStyles({
		backdrop: {
			zIndex: theme.zIndex.drawer + 1,
			color: "#fff",
			position: "absolute"
		}
	})
);

const EnrollmentMapSection: React.FC<EnrollmentMapSectionProps> = ({ refresh, setRefresh }) => {
	const [planCheckedStatus, setPlanCheckedStatus] = useState(false);
	const [selectedCorePlan, setSelectedCorePlan] = useState<string>("Health Plan");
	const [selectedVEBPlan, setSelectedVEBPlan] = useState<string>("Cancer");
	const [_coreMarkers, _setCoreMarkers] = useState<CoreMarkers[]>([]);
	const [_VEBMapMarks, _setVEBMapMarkers] = useState<VEBMapMarkers[]>([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [mapType, setMapType] = useState<string>("ZIP WISE");
	const [loadingState, setLoadingState] = useState<boolean>(false);

	const queryGroupNumber = useRouteParam("gn");
	const tabStyleClass = tabStyles();
	const backdropClasses = backdropStyles();

	const coreTabLabels = [
		"Health Plan",
		"Clinic Card",
		"GSTD",
		"GLTD(Class 1)",
		"GLTD(Class 2)",
		"KSOP",
		"Helping Our Own",
		"GLI",
		"CCE"
	];

	const VEBTabLabels = [
		"Cancer",
		"Whole Life",
		"STD",
		"Critical Illness",
		"Hospital Indemnity",
		"Accident",
		"GLI",
		"Term Life to age 121",
		"Doc & Rx",
		"Vision",
		"Dental"
	];

	const planNamesInitialToFullName = (param: string, constants: any) => {
		if (Object.keys(constants).includes(param)) {
			return constants[param as keyof typeof constants];
		} else if (Object.values(constants).includes(param)) {
			const keys = Object.keys(constants).filter(
				(key: string) => constants[param as keyof typeof constants] === param
			);
			return keys.length > 0 ? keys[0] : param;
		} else {
			return param;
		}
	};

	const handleCoreEnrollmentMapProjectionClick = (event: any, index: any) => {
		const selectedPlanName = event.target.innerText;
		const _selectedPlan = planNamesInitialToFullName(
			selectedPlanName === "GLTD(Class 1)"
				? "GLTD 1"
				: selectedPlanName === "GLTD(Class 2)"
				? "GLTD 2"
				: selectedPlanName,
			BBI_CORE_ENROLLMENT_SHORT_NAMES
		);
		setTabIndex(index);
		setSelectedCorePlan(_selectedPlan as unknown as string);
	};

	const handleVEBEnrollmentMapProjectionClick = (event: any, index: any) => {
		const selectedPlanName = event.target.innerText;
		const _selectedPlan = planNamesInitialToFullName(
			selectedPlanName === "Term Life to age 121" ? BBI_VEB_ENROLLMENT_SHORT_NAMES.FPP : selectedPlanName,
			BBI_VEB_ENROLLMENT_SHORT_NAMES
		);
		setTabIndex(index);
		setSelectedVEBPlan(_selectedPlan as unknown as string);
	};

	const getInitActiveMemberProjectionDetails = useCallback(
		async (queryGroupNumber: number, enrollmentType: string, selectedPlan: string, mapType: string) => {
			const projectionPayload = {
				group_number: queryGroupNumber,
				enrollment_type: enrollmentType,
				plan_name: selectedPlan,
				map_type: mapType
			};

			// api-call
			const response = await api.benefitSummary.getBenefitMemberProjection(projectionPayload);
			const { activeBenefitMemberProjectionInstance }: { activeBenefitMemberProjectionInstance: any[] } =
				response || [];

			if (response)
				setTimeout(() => {
					setLoadingState(false);
				}, 1500);

			// ======================================================= (OPERATION FOR VEB ENROLLMENT ) ==========================

			const _storedCoreMarkersParent: CoreMarkers[] = Array.from({ length: 9 }, () => []);
			const _storedAllCoreLocationCoordinates: any[] = [];

			if (enrollmentType === "CORE") {
				mapType === "ZIP WISE"
					? activeBenefitMemberProjectionInstance.forEach(({ member_count, ZIP }, index) => {
							const _storedSingleLocationCoordinates: any[] = [];
							const _processedCoordinates = zipToCoordinateConverter(ZIP);
							_storedSingleLocationCoordinates.push(..._processedCoordinates);

							_storedAllCoreLocationCoordinates.push({
								positionProps: _storedSingleLocationCoordinates,
								nameProps: member_count
							});
					  })
					: mapType === "MEMBER WISE"
					? activeBenefitMemberProjectionInstance.forEach(({ employee_name, ZIP }, index) => {
							const _storedSingleLocationCoordinates: any[] = [];
							const _processedCoordinates = zipToCoordinateConverter(ZIP);
							_storedSingleLocationCoordinates.push(..._processedCoordinates);

							_storedAllCoreLocationCoordinates.push({
								positionProps: _storedSingleLocationCoordinates,
								nameProps: employee_name
							});
					  })
					: null;

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.HP && _storedCoreMarkersParent[0].length === 0)
					_storedCoreMarkersParent[0].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.CC && _storedCoreMarkersParent[1].length === 0)
					_storedCoreMarkersParent[1].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.GSTD && _storedCoreMarkersParent[2].length === 0)
					_storedCoreMarkersParent[2].push(..._storedAllCoreLocationCoordinates);

				if (
					selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES["GLTD 1"] &&
					_storedCoreMarkersParent[3].length === 0
				)
					_storedCoreMarkersParent[3].push(..._storedAllCoreLocationCoordinates);

				if (
					selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES["GLTD 2"] &&
					_storedCoreMarkersParent[4].length === 0
				)
					_storedCoreMarkersParent[4].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.KSOP && _storedCoreMarkersParent[5].length === 0)
					_storedCoreMarkersParent[5].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.HOO && _storedCoreMarkersParent[6].length === 0)
					_storedCoreMarkersParent[6].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.GLI && _storedCoreMarkersParent[7].length === 0)
					_storedCoreMarkersParent[7].push(..._storedAllCoreLocationCoordinates);

				if (selectedPlan === BBI_CORE_ENROLLMENT_SHORT_NAMES.CCE && _storedCoreMarkersParent[8].length === 0)
					_storedCoreMarkersParent[8].push(..._storedAllCoreLocationCoordinates);
			}

			// ======================================================= (OPERATION FOR VEB ENROLLMENT - WIP ) ==========================

			const _storedVEBMarkersParent: VEBMapMarkers[] = Array.from({ length: 11 }, () => []);
			const _storedAllVEBLocationCoordinates: any[] = [];

			if (enrollmentType === "VEB") {
				mapType === "ZIP WISE"
					? activeBenefitMemberProjectionInstance.forEach(({ member_count, ZIP }, index) => {
							const _storedSingleLocationCoordinates: any[] = [];
							const _processedCoordinates = zipToCoordinateConverter(ZIP);
							_storedSingleLocationCoordinates.push(..._processedCoordinates);

							_storedAllVEBLocationCoordinates.push({
								positionProps: _storedSingleLocationCoordinates,
								nameProps: member_count
							});
					  })
					: mapType === "MEMBER WISE"
					? activeBenefitMemberProjectionInstance.forEach(({ employee_name, ZIP }, index) => {
							const _storedSingleLocationCoordinates: any[] = [];
							const _processedCoordinates = zipToCoordinateConverter(ZIP);
							_storedSingleLocationCoordinates.push(..._processedCoordinates);

							_storedAllVEBLocationCoordinates.push({
								positionProps: _storedSingleLocationCoordinates,
								nameProps: employee_name
							});
					  })
					: null;

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.Cancer && _storedVEBMarkersParent[0].length === 0)
					_storedVEBMarkersParent[0].push(..._storedAllVEBLocationCoordinates);

				if (
					selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES["Whole Life"] &&
					_storedVEBMarkersParent[1].length === 0
				)
					_storedVEBMarkersParent[1].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.STD && _storedVEBMarkersParent[2].length === 0)
					_storedVEBMarkersParent[2].push(..._storedAllVEBLocationCoordinates);

				if (
					selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES["Critical Illness"] &&
					_storedVEBMarkersParent[3].length === 0
				)
					_storedVEBMarkersParent[3].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.HI && _storedVEBMarkersParent[4].length === 0)
					_storedVEBMarkersParent[4].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.Accident && _storedVEBMarkersParent[5].length === 0)
					_storedVEBMarkersParent[5].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.GLI && _storedVEBMarkersParent[6].length === 0)
					_storedVEBMarkersParent[6].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.FPP && _storedVEBMarkersParent[7].length === 0)
					_storedVEBMarkersParent[7].push(..._storedAllVEBLocationCoordinates);

				if (
					selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES["Doc & Rx"] &&
					_storedVEBMarkersParent[8].length === 0
				)
					_storedVEBMarkersParent[8].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.Vision && _storedVEBMarkersParent[9].length === 0)
					_storedVEBMarkersParent[9].push(..._storedAllVEBLocationCoordinates);

				if (selectedPlan === BBI_VEB_ENROLLMENT_SHORT_NAMES.Dental && _storedVEBMarkersParent[10].length === 0)
					_storedVEBMarkersParent[10].push(..._storedAllVEBLocationCoordinates);
			}

			// CORE-ENROLLMENTS
			_setCoreMarkers(_storedCoreMarkersParent);
			// VEB-ENROLLMENTS
			_setVEBMapMarkers(_storedVEBMarkersParent);
		},
		[]
	);

	const handlePlanSwitchClick = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const { checked } = event.target;
			setPlanCheckedStatus(checked);
			setTabIndex(0);
			setLoadingState(true);

			if (checked === true) {
				setSelectedVEBPlan("Cancer");
				mapType === "ZIP WISE"
					? await getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							"VEB",
							"Cancer",
							"ZIP WISE"
					  )
					: mapType === "MAP WISE"
					? await getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							"VEB",
							"Cancer",
							"MAP WISE"
					  )
					: null;
			}

			if (checked === false) {
				setSelectedCorePlan("Health Plan");
				mapType === "ZIP WISE"
					? await getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							"CORE",
							"Health Plan",
							"ZIP WISE"
					  )
					: mapType === "MAP WISE"
					? await getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							"CORE",
							"Health Plan",
							"MAP WISE"
					  )
					: null;
			}
		},
		[getInitActiveMemberProjectionDetails, mapType, queryGroupNumber]
	);

	const handleMapTypeChangeClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		const _mapTypeToSet = event.target.value;
		setMapType(_mapTypeToSet);
	};

	useEffect(() => {
		const enrollmentType = planCheckedStatus ? "VEB" : "CORE";
		setLoadingState(true);

		queryGroupNumber && enrollmentType === "CORE"
			? mapType === "ZIP WISE"
				? getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						enrollmentType,
						selectedCorePlan,
						mapType
				  )
				: mapType === "MEMBER WISE"
				? getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						enrollmentType,
						selectedCorePlan,
						mapType
				  )
				: null
			: queryGroupNumber && enrollmentType === "VEB"
			? mapType === "ZIP WISE"
				? getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						enrollmentType,
						selectedVEBPlan,
						mapType
				  )
				: mapType === "MEMBER WISE"
				? getInitActiveMemberProjectionDetails(
						queryGroupNumber as number,
						enrollmentType,
						selectedVEBPlan,
						mapType
				  )
				: null
			: null;

		if (refresh) {
			queryGroupNumber && enrollmentType === "CORE"
				? mapType === "MAP WISE"
					? getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							enrollmentType,
							selectedCorePlan,
							mapType
					  )
					: mapType === "MEMBER WISE"
					? getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							enrollmentType,
							selectedCorePlan,
							mapType
					  )
					: null
				: queryGroupNumber && enrollmentType === "VEB"
				? mapType === "MAP WISE"
					? getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							enrollmentType,
							selectedCorePlan,
							mapType
					  )
					: mapType === "MEMBER WISE"
					? getInitActiveMemberProjectionDetails(
							queryGroupNumber as number,
							enrollmentType,
							selectedCorePlan,
							mapType
					  )
					: null
				: null;
			setRefresh(false);
			// setPageLoaderState(false);
		}
	}, [
		getInitActiveMemberProjectionDetails,
		mapType,
		planCheckedStatus,
		queryGroupNumber,
		refresh,
		selectedCorePlan,
		selectedVEBPlan,
		setRefresh
	]);

	return (
		<div className="enrollment-map-section" id="enrollment-map-section">
			<div className="enrollment-map-section-container">
				<div className="map-section-container">
					<div className="specific-enrollment-map-header-container" id="specific-enrollment-container">
						<div className="header-group">
							<h1 className="specific-enrollment-header-text">
								{planCheckedStatus ? "Member Veb Benefit Projection" : "Member Core Benefit Projection"}
							</h1>
							<RadioGroup
								className="radio-button-group"
								aria-label="mapType"
								value={mapType}
								onChange={handleMapTypeChangeClick}
							>
								<FormControlLabel value="ZIP WISE" control={<GreenRadio />} label="ZIP Wise" />
								<FormControlLabel value="MEMBER WISE" control={<GreenRadio />} label="Member Wise" />
							</RadioGroup>
						</div>

						<div className="switch-container" id="switch-container">
							<Grid className="switch-grid-container" container spacing={1}>
								<Grid className="core-plan-switch-container" item>
									Core Plans
								</Grid>
								<Grid item>
									<GreenSwitch
										checked={planCheckedStatus}
										onChange={handlePlanSwitchClick}
										className="plan-switch-custom"
									/>
								</Grid>
								<Grid className="veb-plan-switch-container" item>
									VEB Plans
								</Grid>
							</Grid>
						</div>
					</div>
					<div className="specific-enrollment-map-container-main" id="specific-enrollment-map-container-main">
						{!planCheckedStatus ? (
							// ============================= CORE ENROLLMENT ======================================
							<div className="map-section-main-container" id="map-section-main-container">
								<div className="tab-container">
									<Tabs
										// orientation="vertical"
										variant="scrollable"
										value={tabIndex}
										onChange={handleCoreEnrollmentMapProjectionClick}
										style={{
											minWidth: "10rem",
											boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
											// borderRadius: "0 0 10px 10px",
											// backgroundColor: "#CBE4DE",
											backgroundColor: "#b9b5f2",
											color: "#2E4F4F"
										}}
										aria-label="Vertical tabs example"
										className="glb-tab-panel-custom"
										TabIndicatorProps={{
											style: {
												border: "none",
												backgroundColor: "#6a63d4"
											}
										}}
									>
										{coreTabLabels.map((label, index) => (
											<Tab
												key={index}
												label={label}
												className={`glb-tab-label ${tabStyleClass.customTab}`}
												{...a11yScrollableProps(index)}
											/>
										))}
									</Tabs>
								</div>
								<div className="map-container">
									{_coreMarkers &&
										_coreMarkers.map((marker, index) => {
											return (
												<ScrollTabPanel key={index} value={tabIndex} index={index}>
													<div className="tab-section-information-container">
														<CustomMapView
															mapType={mapType}
															markers={marker}
															index={index} // Pass index to CustomMapView
														/>
													</div>
												</ScrollTabPanel>
											);
										})}
									<Backdrop className={backdropClasses.backdrop} open={loadingState}>
										<MagnifyingGlass
											visible={true}
											height="80"
											width="80"
											ariaLabel="MagnifyingGlass-loading"
											wrapperStyle={{}}
											wrapperClass="MagnifyingGlass-wrapper"
											glassColor="#ffe7a6"
											color="#8b97f3"
										/>
									</Backdrop>
								</div>
							</div>
						) : (
							// ============================= VEB ENROLLMENT ======================================
							<div className="map-section-main-container" id="map-section-main-container">
								<div className="tab-container">
									<Tabs
										// orientation="vertical"
										variant="scrollable"
										value={tabIndex}
										onChange={handleVEBEnrollmentMapProjectionClick}
										style={{
											minWidth: "10rem",
											boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 0px 2.6px",
											// borderRadius: "0 0 10px 10px",
											backgroundColor: "#b9b5f2",
											color: "#2E4F4F"
										}}
										aria-label="Vertical tabs example"
										className="glb-tab-panel-custom"
										TabIndicatorProps={{
											style: {
												border: "none",
												backgroundColor: "#6a63d4"
											}
										}}
									>
										{VEBTabLabels.map((label, index) => (
											<Tab
												key={index}
												label={label}
												className={`glb-tab-label ${tabStyleClass.customTab}`}
												{...a11yScrollableProps(index)}
											/>
										))}
									</Tabs>
								</div>
								<div className="map-container">
									{_VEBMapMarks &&
										_VEBMapMarks.map((marker, index) => {
											return (
												<ScrollTabPanel key={index} value={tabIndex} index={index}>
													<div className="tab-section-information-container">
														<CustomMapView
															mapType={mapType}
															markers={marker}
															index={index} // Pass index to CustomMapView
														/>
													</div>
												</ScrollTabPanel>
											);
										})}
									<Backdrop className={backdropClasses.backdrop} open={loadingState}>
										<MagnifyingGlass
											visible={true}
											height="80"
											width="80"
											ariaLabel="MagnifyingGlass-loading"
											wrapperStyle={{}}
											wrapperClass="MagnifyingGlass-wrapper"
											glassColor="#ffe7a6"
											color="#8b97f3"
										/>
									</Backdrop>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default EnrollmentMapSection;
